/**
 * COPY STRATEGY — DriverTaxesPage
 *
 * Awareness:      Problem-Aware (Stage 2/5)
 *                 Reader knows they pay taxes but doesn't understand the specifics.
 *                 Confused about GPM, VSD, PSD, PVM — how much, when, and why.
 *
 * Sophistication: Stage 2 — Enlarged Claim
 *                 "2-3x daugiau GPM nei turėtų" — enlarged problem statement.
 *                 Comprehensive overview article, the "pillar" content piece.
 *
 * Avatar:         New or confused driver, first year of individuali veikla.
 *                 Needs a complete tax overview. Searching "mokesčiai pavežėjui"
 *                 or "bolt mokesciai". Entry-level knowledge.
 *
 * Hook:           Overpayment shock — "moka 2-3x daugiau GPM nei turėtų"
 * Structure:      Overview → GPM deep dive → VSD → PSD → PVM → Tax credit → CTA
 * Proof:          Specific rates, thresholds, and calculation examples per tax type
 * Funnel:         Pillar article — links out to every specialized topic
 */
import { motion } from 'framer-motion'
import { PiWarning } from 'react-icons/pi'
import { Link } from 'react-router-dom'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const DriverTaxesPage = () => {
  usePageSetup('Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM gidas', {
    scroll: true,
    description: 'Bolt ir Uber vairuotojų mokesčiai 2026 m.: GPM (5–20%), VSD (12,52%), PSD (6,98%) ir atvirkštinis PVM. Daugelis moka 2–3× daugiau nei turėtų — sužinokite, kodėl.',
    type: 'article',
    datePublished: '2025-11-05',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Mokesčiai</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">7 min skaitymo</span>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-2"
        >
          Dauguma Bolt ir Uber pavežėjų moka 2-3x daugiau GPM nei turėtų. Kodėl? Nes nežino apie mokesčio kreditą, kuris sumažina GPM mokestį nuo 20% iki 5%.
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-3"
        >
          Šiame straipsnyje detaliai išanalizuosime kiekvieną Bolt mokestį — GPM, PSD, VSD ir PVM — jų dydį, mokėjimo terminus ir kaip galite susigrąžinti pinigų.
        </MotionText>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          1. GPM (Gyventojų pajamų mokestis)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            5-20% nuo grynųjų pajamų (progresinis)
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-sm mb-3"
          >
            GPM skaičiuojamas nuo apmokestinamųjų pajamų – tai reiškia, kad iš gautų pajamų galite atimti išlaidas (faktines arba 30% normą).
          </MotionText>
          <MotionDiv className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-3">
            <MotionText as="p" className="text-blue-900 text-sm font-medium mb-2">
              Kaip veikia progresinis tarifas (2026 m.):
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs mb-2">
              <strong>Bazinis tarifas:</strong> 20% (nuo 2026 m., GPMĮ 6 str.)
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs mb-3">
              <strong>Mokesčio kreditas:</strong> 15% lengvata pelnui iki 20 000 € (GPMĮ 18² str.)
            </MotionText>
            <MotionText as="p" className="text-blue-900 text-xs font-medium mb-1">
              Efektyvus tarifas:
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs mb-1">
              • Pelnas iki 20 000 € → <strong>5%</strong> (20% - 15% kreditas = 5%)
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs mb-1">
              • Pelnas 20 001 - 42 500 € → <strong>5-20%</strong> (kreditas mažėja)
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs">
              • Pelnas virš 42 500 € → <strong>20-32%</strong> (progresinis be kredito)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-2">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Pajamos: 2000 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Išlaidos: 600 € (30 % norma arba faktinės)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2">
              Apmokestinamosios pajamos: 1400 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium">
              GPM (5%, nes {'<'} 20k €/metais): 70 €
            </MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs mt-1">
              Jei metinės pajamos viršytų 42,5k €, tarifas didėtų iki 20-32%
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl mb-4">
          <MotionText as="h4" className="text-green-900 text-sm font-medium mb-2">
            💡 Svarbu pavežėjams!
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-2">
            5% tarifas taikomas <strong>apmokestinamajam pelnui</strong>, o ne bendrosioms pajamoms.
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-2">
            Su 30% išlaidų norma galite gauti iki <strong>28 571 € bruto pajamų</strong> per metus ir vis tiek mokėti tik 5% GPM:
          </MotionText>
          <MotionText as="p" className="text-green-700 text-xs">
            28 571 € pajamos - 30% (8 571 €) = 20 000 € pelnas → 5% tarifas
          </MotionText>
        </MotionDiv>

        <MotionDiv className="mb-4">
          <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-1">
            Mokėjimo terminas
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            Kasmet iki gegužės 2 d. pateikiama metinė GPM deklaracija ir sumokamas mokestis.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          2. PSD (Privalomasis sveikatos draudimas)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            6,98% nuo pajamų
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-sm mb-3"
          >
            PSD užtikrina prieigą prie gydytojų ir ligoninių. Mokamas dviem etapais:
          </MotionText>
          <MotionDiv className="space-y-3">
            <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-1">
                1. Mėnesinis minimalus PSD
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">
                6,98 % nuo MMA. Mokama kas mėnesį iki mėnesio pabaigos.
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mt-2">
                <strong>2025 m.:</strong> 1 038 € × 6,98 % = 72,45 €/mėn.
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono">
                <strong>2026 m.:</strong> 1 153 € × 6,98 % = 80,48 €/mėn.
              </MotionText>
            </MotionDiv>

            <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-1">
                2. Metinis likutinis PSD
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">
                Jei metinės apmokestinamosios pajamos viršijo 12 MMA, permoka perskaičiuojama ir sumokama iki kitų metų gegužės 2 d.
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mt-2">
                Pavyzdys: jei per metus uždirbote 15 000 €, metinis PSD = 15 000 € × 6,98 % = 1 047 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono">
                Jau sumokėta: 80,48 € × 12 = 965,76 €
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium">
                Likutis: 1 047 € - 965,76 € = 81,24 €
              </MotionText>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-900 text-sm mb-2">
            <strong>PSD atostogos:</strong> galite nemokėti PSD už tą mėnesį, kai nedirbate ir neturite pajamų.
          </MotionText>
          <MotionText as="p" className="text-blue-900 text-sm mb-2">
            <strong>Senatvės pensininkai:</strong> asmenys, sulaukę senatvės pensijos amžiaus, yra atleisti nuo PSD mokėjimo.
          </MotionText>
          <MotionText as="p" className="text-blue-900 text-sm">
            <strong>Studentai:</strong> dieninių studijų studentai yra draudžiami valstybės lėšomis ir PSD mokėti neprivalo.
          </MotionText>
        </MotionDiv>

        <MotionDiv className="p-4 bg-zinc-50 border-l-4 border-zinc-700 rounded-r-xl">
          <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
            🔍 Kaip patikrinti savo mokesčių tarifą?
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            Naudokite oficialų VMI/Sodros skaičiuoklę ir įsitikinkite patys:
          </MotionText>
          <ol className="space-y-2">
            <li className="text-zinc-700 text-sm">
              1. Eikite į <strong>sodra.lt</strong> arba <strong>vmi.lt</strong> skaičiuoklę
            </li>
            <li className="text-zinc-700 text-sm">
              2. Įveskite "Gautos pajamos": pvz., 10 000 €
            </li>
            <li className="text-zinc-700 text-sm">
              3. Pasirinkite "30% išlaidų norma"
            </li>
            <li className="text-zinc-700 text-sm">
              4. Pažiūrėkite "GPM" eilutę - matysite 350 €
            </li>
          </ol>
          <MotionText as="p" className="text-zinc-600 text-xs mt-3">
            Patikrinimas: 10 000 € - 30% = 7 000 € pelnas. 7 000 € × 5% = 350 € ✓
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          3. VSD (Valstybinio socialinio draudimo įmokos)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            12,52% (arba 15,52% su pensijos kaupimu)
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-sm mb-3"
          >
            VSD užtikrina pensiją, ligos ir motinystės išmokas. Mokamas kartą per metus nuo <strong>90% apmokestinamųjų pajamų</strong>.
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Metinės pajamos: 24 000 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Išlaidos: 7 200 € (30 % norma)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2">
              Apmokestinamosios pajamos: 16 800 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              VSD bazė (90%): 16 800 € × 0,9 = 15 120 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium">
              VSD (12,52 %): 15 120 € × 12,52% = 1 893 €
            </MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs mt-1">
              Su pensijos kaupimu (15,52%): 2 349 €
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="mb-4">
          <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-1">
            Mokėjimo terminas
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            Iki gegužės 2 d. (2026 m.) kartu su metine GPM deklaracija. Galima mokėti ir kas mėnesį, kad greičiau gautumėte išmokas.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          4. Atvirkštinis PVM
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            21 % nuo „Bolt“ / „Uber“ komisinio mokesčio
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-sm mb-3"
          >
            Kai perkate paslaugas iš kitos ES šalies įmonės (pvz., „Bolt“ komisinį), jūs patys turite deklaruoti ir sumokėti PVM.
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-2">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Komisinis mokestis: 500 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium">
              Atvirkštinis PVM (21 %): 105 €
            </MotionText>
          </MotionDiv>
          <MotionDiv className="bg-green-50 border border-green-200 rounded-lg p-3">
            <MotionText as="p" className="text-green-900 text-sm">
              ✅ Šis PVM įtraukiamas į išlaidas, todėl sumažina GPM.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="mb-4">
          <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-1">
            Mokėjimo terminas
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            Kiekvieną mėnesį iki 25 d. už praėjusio mėnesio komisinį.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          5. Taršos mokestis (jei taikoma)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Tik jei automobilio CO₂ emisija viršija normą
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            Taršos mokestis taikomas tik automobiliams, kurių CO₂ emisija viršija nustatytą normą. Dauguma elektromobilių ir hibridų šio mokesčio nemoka.
          </MotionText>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Kaip susigrąžinti pinigų
              </MotionText>
              <ul className="space-y-2">
                <li className="text-amber-800 text-sm">
                  • Rinkitės faktines išlaidas, jei jos viršija 30 % pajamų
                </li>
                <li className="text-amber-800 text-sm">
                  • Saugokite visus kuro, remonto ir draudimo kvitus
                </li>
                <li className="text-amber-800 text-sm">
                  • Įtraukite atvirkštinį PVM į išlaidas
                </li>
                <li className="text-amber-800 text-sm">
                  • Užsirašykite visas su veikla susijusias išlaidas
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h4" className="text-blue-900 text-sm font-medium mb-2">
            📚 Teisiniai pagrindai
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-xs mb-2">
            <strong>GPM tarifas:</strong> Lietuvos Respublikos gyventojų pajamų mokesčio įstatymas (GPMĮ)
          </MotionText>
          <ul className="space-y-1">
            <li className="text-blue-700 text-xs">
              • 6 straipsnis, 1 dalis - Bazinis 20% tarifas (nuo 2026 m.)
            </li>
            <li className="text-blue-700 text-xs">
              • 18² straipsnis, 2 dalis - Pajamų mokesčio kreditas (15% lengvata nuo 2026 m.)
            </li>
          </ul>
          <MotionText as="p" className="text-blue-600 text-xs mt-2">
            Oficialus šaltinis: e-seimas.lrs.lt (ieškokite "Gyventojų pajamų mokesčio įstatymas")
          </MotionText>
        </MotionDiv>

        <MotionDiv className="p-4 bg-zinc-50 border-l-4 border-zinc-700 rounded-r-xl mt-8 mb-8">
          <MotionText as="h2" className="text-zinc-900 text-base font-medium mb-2">
            Kada mokėti šiuos mokesčius?
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            PVM — kas mėnesį iki 25 d. PSD — kas mėnesį iki mėnesio pabaigos. GPM ir VSD — kasmet iki gegužės 2 d.
          </MotionText>
          <Link to="/blog/mokesciu-kalendorius-kada-moketi-mokescius" className="text-blue-600 text-sm font-medium underline">
            Pilnas mokesčių kalendorius 2026 →
          </Link>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2026: kada mokėti?' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos?' },
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM už Bolt komisinius' },
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default DriverTaxesPage
